import React, { useState, useEffect } from "react";
import { Box, Checkbox, styled } from "@mui/material";
import { ProjectTypeItem, getProjectTypeData } from "../data/projectTypeData";
import { getStaticFileUrl } from "../utils/config";

// ============================ (원본 스타일 유지) ============================ //

const ContactItem = styled("div")(() => ({
  marginTop: "50px",
  "@media (max-width: 1000px)": {
    marginTop: "30px",
  },
}));

const ContactLabel = styled("div")(() => ({
  marginBottom: "20px",
  paddingLeft: "28px",
  background:
    `url(${getStaticFileUrl('/img/contact/check_icon.png')}) no-repeat left top 4px / 18px`,
  color: "#fff",
  fontSize: "22px",
  fontWeight: 500,
  lineHeight: "26px",

  "@media (max-width: 1000px)": {
    fontSize: "16px",
    lineHeight: "18px",
    backgroundPosition: "left top",
  },
}));

const CheckListWrapper = styled(Box)(() => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
}));

const GroupBox = styled(Box)(() => ({
  display: "inline-block",
  width: "calc((100% - 24px) / 3)", // 원본 스타일 유지
  verticalAlign: "top",
  marginLeft: "12px",

  "&:first-of-type": {
    marginLeft: 0,
  },

  "@media (max-width: 1000px)": {
    display: "block",
    width: "100%",
    marginLeft: 0,
    marginTop: "30px",
  },
}));

const GroupTitle = styled(Box)(() => ({
  marginBottom: "20px",
  paddingTop: "10px",
  paddingBottom: "10px",
  borderBottom: "1px solid #fff",
  color: "#fff",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "30px",

  "@media (max-width: 1000px)": {
    marginBottom: 0,
    paddingLeft: "14px",
    borderBottom: 0,
    borderRadius: "4px 0 0 4px",
    fontSize: "14px",
  },
}));

const CheckItem = styled(Box)(() => ({
  position: "relative",
  marginTop: "8px",

  "@media (max-width: 1000px)": {
    marginTop: 0,
  },
}));

const CheckboxLabel = styled("label")<{ checked: boolean }>(({ checked }) => ({
  display: "block",
  position: "relative",
  padding: "10px 8px 8px 8px",
  border: "2px solid #2329b6",
  borderRadius: "10px",
  backgroundColor: checked ? "#1d2c90" : "#121640", // ✅ 선택 시 배경 변경
  color: "#fff",
  fontSize: "16px",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: "30px",
  letterSpacing: "-1px",
  cursor: "pointer",
  transition: "0.2s",
  borderColor: checked ? "#343bde" : "#2329b6", // ✅ 선택 시 테두리 변경

  "&:hover": {
    borderColor: "#343bde",
    backgroundColor: "#1d2c90",
  },

  "@media (max-width: 1000px)": {
    border: 0,
    borderRadius: 0,
    paddingLeft: "14px",
    paddingRight: "14px",
    fontSize: "14px",
    fontWeight: 500,
    textAlign: "left",
    lineHeight: "26px",
  },
}));

const HiddenCheckbox = styled(Checkbox)(() => ({
  display: "none",
}));

// ============================ (프로젝트 유형 데이터) ============================ //

// ============================ (컴포넌트) ============================ //

interface Props {
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
}

export const ProjectTypeSelector: React.FC<Props> = ({
  selectedTypes,
  setSelectedTypes,
}) => {
  const [projectTypeData, setProjectTypeData] = useState<ProjectTypeItem[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getProjectTypeData();
        setProjectTypeData(data);
      } catch (error) {
        console.error('프로젝트 타입 데이터 로드 실패:', error);
      }
    };

    loadData();

    // 서버 데이터 변경 감지를 위한 폴링 (선택적)
    const interval = setInterval(() => {
      loadData();
    }, 30000); // 30초마다 새로고침

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleCheck = (label: string) => {
    setSelectedTypes(
      selectedTypes.includes(label)
        ? selectedTypes.filter((t) => t !== label)
        : [...selectedTypes, label]
    );
  };

  return (
    <ContactItem>
      <ContactLabel>[필수] 프로젝트 유형</ContactLabel>

      <CheckListWrapper>
        {projectTypeData.map((group) => (
          <GroupBox key={group.title}>
            <GroupTitle>{group.title}</GroupTitle>

            <CheckListWrapper>
              {group.children.map((child) => {
                const checked = selectedTypes.includes(child.label);
                return (
                  <CheckItem key={child.id}>
                    <HiddenCheckbox
                      id={child.id}
                      checked={checked}
                      onChange={() => handleCheck(child.label)}
                      name="cont[project][type][]"
                    />
                    {/* ✅ 선택 시 hover 효과 유지 */}
                    <CheckboxLabel htmlFor={child.id} checked={checked}>
                      {child.label}
                    </CheckboxLabel>
                  </CheckItem>
                );
              })}
            </CheckListWrapper>
          </GroupBox>
        ))}
      </CheckListWrapper>
    </ContactItem>
  );
};
